import { forwardRef } from 'react'
import * as React from 'react'
import styled, { css } from 'styled-components'
import Typography, { TYPOGRAPHY_ICON_SIZE_CSS_VAR, TYPOGRAPHY_LINE_HEIGHT_CSS_VAR } from './Typography'
import clsx from 'clsx'
import { rem } from 'polished'
import { setCSSVar } from 'lib/theme/themeUtils'
import { mediaQueryUp } from 'components/utils/breakpoint'

export const CaptionLarge = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '20px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(16))}
  font-size: 14px;
`

export const CaptionMedium = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '16px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(16))}
  font-size: 12px;
`

export const CaptionSmall = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '14px')}
  /* Keep at 16 as icons don't display clear enough below that */
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(16))}
  font-size: 11px;
  font-weight: ${props => props.theme.font.primary.weight.bold};
  letter-spacing: 0.7px;
`

export const CaptionSmallAlt = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '14px')}
  /* Keep at 16 as icons don't display clear enough below that */
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(16))}
  font-size: 11px;
  letter-spacing: 0.2px;
`

/*
  Font sizes are express in px as they are not to be affected by the
  20% REM font decrease at mobile sizes. These font sizes are absolute sizes.
*/
const Element = styled(Typography)`
  font-family: ${props => props.theme.font.primary.family};

  &.variant-large {
    ${CaptionLarge}
  }

  &.variant-large:is(small),
  &.variant-large small,
  &.variant-medium {
    ${CaptionMedium}
  }

  &.variant-medium:is(small),
  &.variant-medium small,
  &.variant-small,
  &.variant-small:is(small),
  &.variant-small small {
    ${CaptionSmall}
  }

  &.variant-smallalt,
  &.variant-smallalt:is(small),
  &.variant-smallalt small {
    ${CaptionSmallAlt}
  }

  /* Tablet breakpoint variants */
  ${mediaQueryUp.tablet} {
    &.variant-tablet-large {
      ${CaptionLarge}
    }

    &.variant-tablet-large:is(small),
    &.variant-tablet-large small,
    &.variant-tablet-medium {
      ${CaptionMedium}
    }

    &.variant-tablet-medium:is(small),
    &.variant-tablet-medium small,
    &.variant-tablet-small,
    &.variant-tablet-small:is(small),
    &.variant-tablet-small small {
      ${CaptionSmall}
    }

    &.variant-tablet-smallalt,
    &.variant-tablet-smallalt:is(small),
    &.variant-tablet-smallalt small {
      ${CaptionSmallAlt}
    }
  }

  /* Desktop breakpoint variants */
  ${mediaQueryUp.desktop} {
    &.variant-desktop-large {
      ${CaptionLarge}
    }

    &.variant-desktop-large:is(small),
    &.variant-desktop-large small,
    &.variant-desktop-medium {
      ${CaptionMedium}
    }

    &.variant-desktop-medium:is(small),
    &.variant-desktop-medium small,
    &.variant-desktop-small,
    &.variant-desktop-small:is(small),
    &.variant-desktop-small small {
      ${CaptionSmall}
    }

    &.variant-desktop-smallalt,
    &.variant-desktop-smallalt:is(small),
    &.variant-desktop-smallalt small {
      ${CaptionSmallAlt}
    }
  }

  &.weight-normal { font-weight: ${props => props.theme.font.primary.weight.regular}; }
  &.weight-bold, &:is(b, strong), b, strong { font-weight: ${props => props.theme.font.primary.weight.bold}; }
  &.italics, &:is(i, em), i, em { font-style: italic; }
  &.underline { text-decoration: underline; }
  &.strike-through, &:is(s), s { text-decoration: line-through; }
`

type Variant = 'smallalt' | 'small' | 'medium' | 'large'
interface Props extends Omit<React.ComponentProps<typeof Typography>, 'forwardedAs'> {
  variant: Variant;
  tabletVariant?: Variant;
  desktopVariant?: Variant;
  weight?: 'normal' | 'bold'
  italics?: boolean
  underline?: boolean
  strikeThrough?: boolean
}

const Caption = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    variant,
    tabletVariant,
    desktopVariant,
    weight,
    italics,
    underline,
    strikeThrough,
    as,
    className,
    ...rest
  } = props

  // Cascade: tablet defaults to variant, desktop defaults to tabletVariant
  const resolvedTabletVariant = tabletVariant ?? variant
  const resolvedDesktopVariant = desktopVariant ?? resolvedTabletVariant

  return <Element
    {...rest}
    ref={ref}
    forwardedAs={as}
    className={clsx(
      className,
      `variant-${variant}`,
      `variant-tablet-${resolvedTabletVariant}`,
      `variant-desktop-${resolvedDesktopVariant}`,
      weight ? `weight-${weight}` : undefined,
      {
        italics,
        underline,
        'strike-through': strikeThrough,
      },
    )}
  />
})

Caption.displayName = 'Caption'

export default Caption
